import { useCallback, useLayoutEffect, useMemo, useRef, useState, type ReactNode } from 'react';
import { Filter, Download, type LucideIcon } from 'lucide-react';
import { tv } from 'tailwind-variants';
import { useTranslation } from 'react-i18next';
import { AdminProfileButton } from './AdminProfileButton.js';
import { AdminProfileSheet } from './AdminProfileSheet.js';
import { AdminSettingsButton } from './AdminSettingsButton.js';
import { AdminSettingsSheet } from './AdminSettingsSheet.js';
import { EnterpriseNavIconBadge } from './EnterpriseNavIconBadge.js';
import { enterpriseHeaderPill } from './enterpriseHeader.styles.js';
import { FullPageExportMenu } from './FullPageExportMenu.js';
import { OverflowMenu, type OverflowMenuItem } from './OverflowMenu.js';
import { shellPageTitle } from './panelHeader.styles.js';
import { Button } from './surfacePrimitives.js';

function shouldShowDateRange(dateRangeLabel: string | undefined, fallbackLabel: string): boolean {
  if (dateRangeLabel == null || dateRangeLabel.trim() === '') {
    return false;
  }
  return dateRangeLabel !== fallbackLabel;
}

/** FIX 7: compact wraps naturally on word boundaries (no line-clamp / ellipsis). md+ truncate/nowrap. */
const TITLE_WRAP_COMPACT =
  'min-w-0 whitespace-normal break-normal [overflow-wrap:normal] [word-break:normal] [hyphens:none]';
const TITLE_OVERFLOW_MD = 'md:truncate md:whitespace-nowrap';

const header = tv({
  base: [
    // Spec A: one-band title+actions (no mobile order-1/order-2 stack).
    // FIX 5 / Fix 7 G8: compact min-height floor via --admin-header-compact-min-height (84px).
    // Do NOT bind min-h to the live-published --admin-header-compact-height — that ratchet
    // locks a temporary wrap/grow height and leaves a tall blank band under the title.
    // Desktop: md:min-h-0 / md:h-auto / md:max-h-none — hug content only.
    // gap-x-1 (not gap-x-2): reclaim 4px so compact title words fit beside Overflow+Settings+Profile.
    'flex flex-row flex-nowrap items-center justify-between gap-x-1',
    'min-h-[var(--admin-header-compact-min-height)]',
    'md:min-h-0 md:h-auto md:max-h-none md:flex-wrap md:items-center md:overflow-visible',
    'md:gap-x-[var(--space-6)]',
    'border-b border-[var(--color-rail-card-border)] bg-[var(--color-an-surface)]',
    // Margin (not padding) clears the fixed TEST economy band so title never shares its paint box.
    'mt-[var(--admin-test-economy-chrome-height,0px)]',
    'px-[var(--space-4)] py-2 pt-[max(var(--space-2),var(--safe-area-inset-top))] md:px-[var(--space-6)] md:py-2 md:pt-2',
  ].join(' '),
});

const titleBlock = tv({
  base: 'min-w-0 flex-1',
});
const subtitle = tv({
  // Spec §6.2 / G9: hide subtitle on compact when collision/compact (default); md+ show.
  base: 'mt-1 hidden truncate text-sm text-[var(--color-an-text-muted)] md:block',
});
const controls = tv({
  base: 'flex shrink-0 flex-nowrap items-center justify-end gap-1 md:flex-wrap md:gap-2',
});
const userChip = tv({
  // Compact right chrome = profile/settings/overflow only — chip is md+.
  base: 'hidden items-center gap-2 rounded-full border border-[var(--color-rail-card-border)] bg-[var(--color-an-surface-muted)] py-1 pl-1 pr-3 text-sm text-[var(--color-an-text)] md:inline-flex',
});
const compareSelect = tv({
  base: 'min-h-11 rounded-lg border border-[var(--color-rail-card-border)] bg-[var(--color-an-surface)] px-3 py-2 text-sm text-[var(--color-an-text)]',
});
const avatar = tv({
  base: 'flex h-8 w-8 items-center justify-center rounded-full bg-[var(--color-an-primary)] text-xs font-bold text-[var(--color-an-on-primary,#ffffff)]',
});

export type EnterpriseHeaderCompareMode = 'previous_period' | 'none';

/**
 * Sacred page chrome — free of Obnovit.
 * Always-on: settings / profile / notifications (when slotted).
 * Filtry / Export: documented export-filter shell contract when callers
 * (`DashboardView`) wire `onFilterClick` / `onExportClick`; omit when unwired.
 * Tab-specific list refresh → `ScreenContentActions` + `ScreenRefreshButton` under KPIs.
 */
export interface EnterprisePageHeaderProps {
  readonly title: string;
  readonly titleIcon?: LucideIcon;
  readonly subtitle?: string;
  /**
   * When true (default), hide subtitle under compact viewport to protect compact density.
   * Set false only when a screen must keep lead copy on mobile without collision.
   */
  readonly hideSubtitleOnCompact?: boolean;
  readonly dateRangeLabel?: string;
  /** Optional interactive date control. Wins over dateRangeLabel. */
  readonly dateRangeSlot?: ReactNode;
  /**
   * Primary working-context control under the title (e.g. inventory kiosk switcher).
   * Square / Shopify pattern: location lives with the page title, not buried in list chrome.
   */
  readonly contextSlot?: ReactNode;
  /** Compare mode label shown when compare control is wired (analytics). */
  readonly compareLabel?: string;
  readonly compareMode?: EnterpriseHeaderCompareMode;
  readonly onCompareModeChange?: (mode: EnterpriseHeaderCompareMode) => void;
  /**
   * Filtry — documented export-filter shell contract (wired by `DashboardView` when the
   * active tab exposes filters). Omit when unwired. Not a redesign surface.
   */
  readonly onFilterClick?: () => void;
  readonly filterLabel?: string;
  /**
   * Export — documented export-filter shell contract (wired by `DashboardView` when the
   * active tab exposes export). Omit when unwired. Not a redesign surface.
   * List/CSV only — do not reuse for full-page PDF/PNG (`fullPageExport`).
   */
  readonly onExportClick?: () => void;
  readonly exportDisabled?: boolean;
  readonly exportDisabledTitle?: string;
  readonly exportLabel?: string;
  /**
   * Full-page PDF/PNG share/export (FIX6-B). Separate from `onExportClick` (list/CSV).
   * Omit on platform/dev headers — share icon renders only when set.
   */
  readonly fullPageExport?: {
    readonly tenantCode: string;
    readonly screenSlug: string;
  };
  /** Notification bell + dropdown from the notifications feature (slot only). */
  readonly notificationsSlot?: ReactNode;
  readonly userName?: string;
  readonly userRole?: string;
  /** When false, hides the avatar/name/role chip (SuperDev shell keeps controls only). */
  readonly showUserChip?: boolean;
  readonly scopeChipLabel?: string;
  /**
   * Slot for rare non-tab chrome only (e.g. health chip). **Forbidden:** tab-specific
   * Obnovit / list refresh — use `ScreenContentActions` + `ScreenRefreshButton` under KPIs.
   * Header is free of Obnovit; Filtry/Export live only via the export-filter props above.
   */
  readonly toolbarActions?: ReactNode;
  /**
   * @deprecated Language/theme live in Settings sheet — prop ignored.
   * Kept so callers compile until cleaned up.
   */
  readonly showThemeSwitch?: boolean;
  /** @deprecated Prefer AdminSettingsSheet appearance radios. */
  readonly themeSlot?: ReactNode;
  /** Copy-link action in the primary header row (dev Analytika deep links). */
  readonly copyLinkAction?: {
    readonly label: string;
    readonly onClick: () => void;
  };
  /** @deprecated Language lives in Settings sheet — prop ignored. */
  readonly showLanguageToggle?: boolean;
  /** When false, hides the Settings gear (rare fixtures). Default true. */
  readonly showShellSettings?: boolean;
  /**
   * When set, shows the profile icon and Sign out in AdminProfileSheet.
   */
  readonly onLogout?: () => void;
  /** Optional guest Sign in action in AdminProfileSheet (login surfaces). */
  readonly onSignIn?: () => void;
  /**
   * When set, shows Spec §11.2 Můj účet in AdminProfileSheet (under the person icon).
   */
  readonly onOpenAccount?: () => void;
  /** Soukromí — only when a dedicated surface exists (omit if same as onOpenAccount). */
  readonly onOpenPrivacy?: () => void;
  /** Přihlášení a zabezpečení — only when dedicated (omit if same as onOpenAccount). */
  readonly onOpenLoginSecurity?: () => void;
  /** Oznámení — Spec “if exist”; omit when notifications prefs are not available. */
  readonly onOpenNotifications?: () => void;
  readonly logoutTestId?: string;
  readonly logoutLabelKey?: string;
  readonly className?: string;
}

export function EnterprisePageHeader({
  title: titleText,
  titleIcon,
  subtitle: subtitleText,
  hideSubtitleOnCompact = true,
  dateRangeLabel,
  dateRangeSlot,
  contextSlot,
  compareLabel,
  compareMode,
  onCompareModeChange,
  onFilterClick,
  filterLabel,
  onExportClick,
  exportDisabled = false,
  exportDisabledTitle,
  exportLabel,
  fullPageExport,
  notificationsSlot,
  userName,
  userRole,
  showUserChip = true,
  scopeChipLabel,
  toolbarActions,
  showThemeSwitch: _showThemeSwitch = true,
  themeSlot: _themeSlot,
  copyLinkAction,
  showLanguageToggle: _showLanguageToggle = true,
  showShellSettings = true,
  onLogout,
  onSignIn,
  onOpenAccount,
  onOpenPrivacy,
  onOpenLoginSecurity,
  onOpenNotifications,
  logoutTestId,
  logoutLabelKey,
  className,
}: EnterprisePageHeaderProps): JSX.Element {
  void _showThemeSwitch;
  void _themeSlot;
  void _showLanguageToggle;
  const { t } = useTranslation('admin');
  const headerRef = useRef<HTMLElement>(null);
  const [settingsOpen, setSettingsOpen] = useState(false);
  const [profileOpen, setProfileOpen] = useState(false);

  // Mirror AdminBottomNav `--admin-bottom-chrome`: publish LIVE header height onto
  // `--admin-header-compact-height` so sheet top/max-h follow a grown 2-line title.
  // min-h uses the separate immutable floor token — never this live var (no ratchet).
  useLayoutEffect(() => {
    const el = headerRef.current;
    if (el === null) {
      return undefined;
    }
    const root = document.documentElement;
    const previous = root.style.getPropertyValue('--admin-header-compact-height');
    const sync = (): void => {
      root.style.setProperty('--admin-header-compact-height', `${String(el.offsetHeight)}px`);
    };
    sync();
    const ro = new ResizeObserver(sync);
    ro.observe(el);
    return () => {
      ro.disconnect();
      if (previous === '') {
        root.style.removeProperty('--admin-header-compact-height');
      } else {
        root.style.setProperty('--admin-header-compact-height', previous);
      }
    };
  }, []);
  const openSettings = useCallback(() => {
    setProfileOpen(false);
    setSettingsOpen(true);
  }, []);
  const closeSettings = useCallback(() => {
    setSettingsOpen(false);
  }, []);
  const openProfile = useCallback(() => {
    setSettingsOpen(false);
    setProfileOpen(true);
  }, []);
  const closeProfile = useCallback(() => {
    setProfileOpen(false);
  }, []);
  const showProfile =
    onLogout !== undefined || onSignIn !== undefined || onOpenAccount !== undefined;
  const displayName = userName ?? '—';
  const initials = displayName.slice(0, 2).toUpperCase();
  const todayLabel = t('enterpriseHeader.dateRangeToday');
  const showDateRange = shouldShowDateRange(dateRangeLabel, todayLabel);
  const resolvedFilterLabel = filterLabel ?? t('analytics.header.filters');
  const resolvedExportLabel =
    exportLabel ?? t('shared.listToolbar.export', { defaultValue: 'Exportovat' });
  const showCompare = onCompareModeChange !== undefined;
  const overflowAriaLabel = t('enterpriseHeader.overflowMenu', {
    defaultValue: 'Další akce',
  });

  /**
   * Compact overflow: Filtry + CSV / copy-link only.
   * Full-page PDF/PNG is the always-visible 44px Share icon (FIX6 G8) — never overflow items.
   */
  const mobileOverflowItems = useMemo((): readonly OverflowMenuItem[] => {
    const items: OverflowMenuItem[] = [];
    if (onFilterClick !== undefined) {
      items.push({
        id: 'filters',
        label: resolvedFilterLabel,
        testId: 'enterprise-header-overflow-filters',
        onSelect: onFilterClick,
      });
    }
    if (onExportClick !== undefined) {
      items.push({
        id: 'export',
        label: resolvedExportLabel,
        disabled: exportDisabled,
        testId: 'enterprise-header-overflow-export',
        onSelect: onExportClick,
      });
    }
    if (copyLinkAction != null) {
      items.push({
        id: 'copy-link',
        label: copyLinkAction.label,
        testId: 'dev-analytics-copy-link',
        onSelect: copyLinkAction.onClick,
      });
    }
    return items;
  }, [
    copyLinkAction,
    exportDisabled,
    onExportClick,
    onFilterClick,
    resolvedExportLabel,
    resolvedFilterLabel,
  ]);

  /** Notifications are md+ only — compact right = overflow + Share + settings + profile. */
  const notificationsChrome: ReactNode =
    notificationsSlot !== undefined ? (
      <div className="hidden md:contents" data-testid="enterprise-header-notifications-md">
        {notificationsSlot}
      </div>
    ) : null;

  const subtitleClassName = hideSubtitleOnCompact
    ? subtitle()
    : // Spec §5.7 small role — no freestyle text-[13px] invent on shell chrome.
      'mt-0.5 overflow-hidden text-[length:var(--font-size-small)] leading-[var(--line-height-small)] text-[var(--color-an-text-muted)] [display:-webkit-box] [-webkit-box-orient:vertical] [-webkit-line-clamp:2] md:mt-1 md:truncate md:text-sm';

  return (
    <>
    <header ref={headerRef} className={header({ className })} data-testid="enterprise-header">
      <div className={titleBlock()}>
        <h1
          className={shellPageTitle({
            // FIX 7: compact natural wrap (no line-clamp / …); md+ may truncate.
            className: `${TITLE_WRAP_COMPACT} ${TITLE_OVERFLOW_MD}`,
          })}
        >
          <span className="flex min-w-0 items-center gap-x-3">
            {titleIcon ? (
              <EnterpriseNavIconBadge
                icon={titleIcon}
                tone="header"
                size="lg"
                className="shrink-0"
                data-testid="enterprise-header-title-icon"
              />
            ) : null}
            <span
              data-testid="enterprise-header-title-text"
              className={`${TITLE_WRAP_COMPACT} min-w-0 flex-1 ${TITLE_OVERFLOW_MD}`}
            >
              {titleText}
            </span>
          </span>
        </h1>
        {subtitleText ? <p className={subtitleClassName}>{subtitleText}</p> : null}
        {contextSlot !== undefined && contextSlot !== null ? (
          <div
            className="mt-2.5 hidden max-w-full md:block"
            data-testid="enterprise-header-context-slot"
          >
            {contextSlot}
          </div>
        ) : null}
      </div>
      <div className={controls()}>
        {/* Text CTAs → md+; compact right = overflow (Filtry/CSV) + Share + settings + profile. */}
        {dateRangeSlot !== undefined ? (
          <div className="relative hidden md:block" data-testid="enterprise-header-date-range">
            {dateRangeSlot}
          </div>
        ) : null}
        {dateRangeSlot === undefined && showDateRange ? (
          <span
            className={`${enterpriseHeaderPill()} hidden md:inline-flex`}
            data-testid="enterprise-header-date-range"
          >
            {dateRangeLabel}
          </span>
        ) : null}
        {scopeChipLabel != null && scopeChipLabel.trim().length > 0 ? (
          <span
            className={`${enterpriseHeaderPill()} hidden md:inline-flex`}
            data-testid="enterprise-header-scope-chip"
          >
            {scopeChipLabel}
          </span>
        ) : null}
        {showCompare ? (
          <label
            className="hidden items-center gap-1.5 md:inline-flex"
            data-testid="enterprise-header-compare"
          >
            <span className="sr-only">{t('analytics.compare.label')}</span>
            <select
              className={compareSelect()}
              value={compareMode ?? 'previous_period'}
              aria-label={compareLabel ?? t('analytics.compare.label')}
              onChange={(event) => {
                const next = event.target.value;
                if (next === 'none' || next === 'previous_period') {
                  onCompareModeChange(next);
                }
              }}
            >
              <option value="previous_period">
                {t('analytics.compare.previousPeriod')}
              </option>
              <option value="none">{t('analytics.compare.none')}</option>
            </select>
          </label>
        ) : null}
        {onFilterClick !== undefined ? (
          <Button
            type="button"
            intent="ghost"
            size="md"
            onClick={onFilterClick}
            title={resolvedFilterLabel}
            aria-label={resolvedFilterLabel}
            data-testid="enterprise-header-filters"
            className="hidden min-h-11 items-center gap-1.5 md:inline-flex"
          >
            <Filter className="h-4 w-4 shrink-0" aria-hidden="true" />
            {/* Compact: Filtry is an overflow item (G2). md+ keeps icon + label. */}
            <span className="hidden md:inline">{resolvedFilterLabel}</span>
          </Button>
        ) : null}
        {onExportClick !== undefined ? (
          <Button
            type="button"
            intent="secondary"
            size="sm"
            onClick={onExportClick}
            disabled={exportDisabled}
            title={exportDisabled ? (exportDisabledTitle ?? t('enterpriseHeader.exportDisabled')) : resolvedExportLabel}
            data-testid="enterprise-header-export"
            className="hidden shrink-0 md:inline-flex"
          >
            <Download className="h-4 w-4 shrink-0" aria-hidden="true" />
            <span>{resolvedExportLabel}</span>
          </Button>
        ) : null}
        {copyLinkAction != null ? (
          <Button
            type="button"
            intent="ghost"
            size="md"
            onClick={copyLinkAction.onClick}
            data-testid="dev-analytics-copy-link"
            className="hidden min-h-11 md:inline-flex"
          >
            {copyLinkAction.label}
          </Button>
        ) : null}
        {mobileOverflowItems.length > 0 ? (
          <div className="md:hidden">
            <OverflowMenu
              items={mobileOverflowItems}
              ariaLabel={overflowAriaLabel}
              testId="enterprise-header-overflow"
            />
          </div>
        ) : null}
        {notificationsChrome}
        {toolbarActions != null ? (
          <div className="hidden md:contents" data-testid="enterprise-header-toolbar-actions">
            {toolbarActions}
          </div>
        ) : null}
        {fullPageExport != null ? (
          <div className="inline-flex" data-testid="enterprise-header-full-page-export-md">
            <FullPageExportMenu
              tenantCode={fullPageExport.tenantCode}
              screenSlug={fullPageExport.screenSlug}
            />
          </div>
        ) : null}
        {showShellSettings ? (
          <AdminSettingsButton open={settingsOpen} onOpen={openSettings} />
        ) : null}
        {showProfile ? (
          <AdminProfileButton
            onClick={openProfile}
            open={profileOpen}
            signedOut={onLogout === undefined && onSignIn !== undefined}
          />
        ) : null}
        {showUserChip ? (
          <div className={userChip()} data-testid="enterprise-header-user">
            <span className={avatar()}>{initials}</span>
            <span className="hidden sm:inline">
              <span className="block font-medium leading-tight">{displayName}</span>
              {userRole ? (
                <span className="block text-xs text-[var(--color-an-text-muted)]">{userRole}</span>
              ) : null}
            </span>
          </div>
        ) : null}
      </div>
    </header>
    {showShellSettings ? (
      <AdminSettingsSheet open={settingsOpen} onClose={closeSettings} />
    ) : null}
    {showProfile ? (
      <AdminProfileSheet
        open={profileOpen}
        onClose={closeProfile}
        onLogout={onLogout}
        onSignIn={onSignIn}
        onOpenAccount={onOpenAccount}
        onOpenPrivacy={onOpenPrivacy}
        onOpenLoginSecurity={onOpenLoginSecurity}
        onOpenNotifications={onOpenNotifications}
        logoutTestId={logoutTestId}
        logoutLabelKey={logoutLabelKey}
      />
    ) : null}
    </>
  );
}
